import { useTranslation } from 'react-i18next';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { useEnumLabel } from '@/components/platform/useLabel';
import { Icon } from '@/components/icons';
import type { AsyncState } from '@/hooks';
import type { GroupAnalytics } from '@/types/platform';
import { formatCount, formatDateTime } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * Money formatting for the group finance view.
 *
 * Amounts in the analytics projection are native units in the institution's
 * own currency — there is no division anywhere: the number core computed is
 * the number shown. The locale comes from i18n so the same figure reads as
 * `XAF 1 234 567` under fr and `XAF 1,234,567` under en.
 */
function formatMoney(amount: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}

/**
 * The currency the group's fee totals are read in.
 *
 * The totals row carries no currency of its own, so the label comes from the
 * members: the first institution row that reports one, then the analytics
 * readiness rows, then the currency breakdown. null means no member reported
 * at all, and the caller then shows amounts without a currency symbol.
 */
function dominantCurrency(analytics: GroupAnalytics): string | null {
  const fromFeeRows = analytics.financial?.institutions.find((institution) => institution.currency)?.currency ?? null;
  if (fromFeeRows) return fromFeeRows;
  const fromMembers = analytics.institutions.find((institution) => institution.currency)?.currency ?? null;
  if (fromMembers) return fromMembers;
  return analytics.currencyBreakdown[0]?.currency ?? null;
}

/**
 * The finance and analytics tabs — how the group is doing, from the analytics
 * projection core generates at request time.
 *
 * WHAT FINANCE MEANS HERE, AND WHAT IT DOES NOT. The projection carries a
 * currency per institution and the readiness signals around it — and, once
 * core produces it, the per-institution fee picture under `financial`. There
 * are no balances and no transactions anywhere in this contract; the financial
 * block is counts and sums core actually computed, in native units. Every
 * figure shown is either that or an explicit "not available": a row core
 * marked `available: false` says so instead of being reduced to zeros, and a
 * projection without `financial` at all says the phase has not produced it.
 */

export function GroupFinancePanel({
  analyticsState,
  onRetry,
}: {
  analyticsState: AsyncState<GroupAnalytics>;
  onRetry: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const label = useEnumLabel();

  const financial = analyticsState.status === 'ready' ? analyticsState.data.financial : undefined;
  const hasFinancial = financial !== undefined;

  return (
    <Panel title={t('platform.groups.finance.title')} icon="products">
      <p className={styles.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {hasFinancial ? t('platform.groups.finance.noticeWithFees') : t('platform.groups.finance.notice')}
      </p>

      <AsyncPanel
        state={analyticsState}
        onRetry={onRetry}
        isEmpty={(analytics) => analytics.currencyBreakdown.length === 0}
        emptyTitle={t('platform.groups.finance.emptyTitle')}
        emptyBody={t('platform.groups.finance.emptyBody')}
      >
        {(analytics) => (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <caption className={styles.tableCaption}>
                  {t('platform.groups.finance.currenciesCaption', { n: analytics.currencyBreakdown.length })}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">{t('platform.groups.finance.colCurrency')}</th>
                    <th scope="col">{t('platform.groups.finance.colInstitutions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.currencyBreakdown.map((entry) => (
                    <tr key={entry.currency}>
                      <th scope="row" className={styles.tableRowHeader}>
                        {entry.currency}
                      </th>
                      <td>{formatCount(entry.institutionCount, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className={styles.subheading}>{t('platform.groups.finance.membersTitle')}</h3>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">{t('platform.groups.finance.colInstitution')}</th>
                    <th scope="col">{t('platform.groups.finance.colStatus')}</th>
                    <th scope="col">{t('platform.groups.finance.colCurrency')}</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.institutions.map((institution) => (
                    <tr key={institution.institutionId}>
                      <th scope="row" className={styles.tableRowHeader}>
                        {institution.name}
                        <span className={styles.tableSub}>{institution.code}</span>
                      </th>
                      <td>
                        {institution.status ? (
                          <span
                            className={
                              institution.status === 'ACTIVE'
                                ? styles.okTag
                                : institution.status === 'SUSPENDED'
                                  ? styles.dangerTag
                                  : styles.mutedTag
                            }
                          >
                            {label('state.onboarding', institution.status)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={styles.mutedCell}>{institution.currency ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasFinancial && <FinancialSection analytics={analytics} locale={locale} />}
          </>
        )}
      </AsyncPanel>
    </Panel>
  );
}

/**
 * The fee collection picture: group totals and one row per institution.
 *
 * CORE'S FIGURES, NOT DASHBOARD MATH. `expectedFees` is what was billed,
 * `collectedFees` what came in, `outstandingFees` the rest; the rate is a
 * percentage core computed, or null when nothing was billed at all — rendered
 * as "nothing billed", never as 0%. A member whose tenant did not report is
 * `available: false` and shown with a label, not zeros, and a figure core did
 * not send stays an em dash. The members' names come from the analytics rows
 * keyed by id — the fee rows carry only ids — and an id the projection does
 * not name is shown as the id itself rather than guessed at.
 */
function FinancialSection({ analytics, locale }: { analytics: GroupAnalytics; locale: string }) {
  const { t } = useTranslation();
  const financial = analytics.financial;

  if (!financial) return null;

  const currency = dominantCurrency(analytics);
  const memberName = (institutionId: string): string => {
    const member = analytics.institutions.find((candidate) => candidate.institutionId === institutionId);
    return member ? member.name : institutionId;
  };
  const memberCode = (institutionId: string): string | undefined =>
    analytics.institutions.find((candidate) => candidate.institutionId === institutionId)?.code;

  const feeCell = (value: number | undefined | null, rowCurrency: string | null | undefined) =>
    value === undefined || value === null ? (
      <span className={styles.mutedCell}>—</span>
    ) : (
      formatMoney(value, rowCurrency ?? currency ?? 'XAF', locale)
    );

  const rateCell = (rate: number | null | undefined) =>
    rate === undefined || rate === null ? (
      <span className={styles.mutedCell}>
        {t('platform.groups.finance.noRate')}
      </span>
    ) : (
      t('platform.groups.finance.rateValue', { rate })
    );

  return (
    <>
      <h3 className={styles.subheading}>{t('platform.groups.finance.feesTitle')}</h3>
      {financial.institutions.length === 0 ? (
        <p className={styles.sectionEmpty}>
          {t('platform.groups.finance.noFeesTitle')}
          <br />
          {t('platform.groups.finance.noFeesBody')}
        </p>
      ) : (
        <>
          {financial.totals && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <caption className={styles.tableCaption}>{t('platform.groups.finance.totalsTitle')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('platform.groups.finance.colStudents')}</th>
                    <th scope="col">{t('platform.groups.finance.colStaff')}</th>
                    <th scope="col">{t('platform.groups.finance.colExpected')}</th>
                    <th scope="col">{t('platform.groups.finance.colCollected')}</th>
                    <th scope="col">{t('platform.groups.finance.colOutstanding')}</th>
                    <th scope="col">{t('platform.groups.finance.colRate')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{formatCount(financial.totals.students, locale)}</td>
                    <td>{formatCount(financial.totals.staff, locale)}</td>
                    <td>{currency ? formatMoney(financial.totals.expectedFees, currency, locale) : formatCount(financial.totals.expectedFees, locale)}</td>
                    <td>{currency ? formatMoney(financial.totals.collectedFees, currency, locale) : formatCount(financial.totals.collectedFees, locale)}</td>
                    <td>{currency ? formatMoney(financial.totals.outstandingFees, currency, locale) : formatCount(financial.totals.outstandingFees, locale)}</td>
                    <td className={styles.mutedCell}>
                      {financial.totals.collectionRate === null
                        ? t('platform.groups.finance.noRate')
                        : t('platform.groups.finance.rateValue', { rate: financial.totals.collectionRate })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">{t('platform.groups.finance.colInstitution')}</th>
                  <th scope="col">{t('platform.groups.finance.colStudents')}</th>
                  <th scope="col">{t('platform.groups.finance.colStaff')}</th>
                  <th scope="col">{t('platform.groups.finance.colExpected')}</th>
                  <th scope="col">{t('platform.groups.finance.colCollected')}</th>
                  <th scope="col">{t('platform.groups.finance.colOutstanding')}</th>
                  <th scope="col">{t('platform.groups.finance.colRate')}</th>
                  <th scope="col">{t('platform.groups.finance.colFeeCurrency')}</th>
                </tr>
              </thead>
              <tbody>
                {financial.institutions.map((institution) => (
                  <tr key={institution.institutionId}>
                    <th scope="row" className={styles.tableRowHeader}>
                      {memberName(institution.institutionId)}
                      {memberCode(institution.institutionId) && (
                        <span className={styles.tableSub}>{memberCode(institution.institutionId)}</span>
                      )}
                    </th>
                    {institution.available ? (
                      <>
                        <td>
                          {institution.students === undefined ? (
                            <span className={styles.mutedCell}>—</span>
                          ) : (
                            formatCount(institution.students, locale)
                          )}
                        </td>
                        <td>
                          {institution.staff === undefined ? (
                            <span className={styles.mutedCell}>—</span>
                          ) : (
                            formatCount(institution.staff, locale)
                          )}
                        </td>
                        <td>{feeCell(institution.expectedFees, institution.currency)}</td>
                        <td>{feeCell(institution.collectedFees, institution.currency)}</td>
                        <td>{feeCell(institution.outstandingFees, institution.currency)}</td>
                        <td>{rateCell(institution.collectionRate)}</td>
                        <td className={styles.mutedCell}>{institution.currency ?? '—'}</td>
                      </>
                    ) : (
                      <td colSpan={7} className={styles.mutedCell}>
                        {t('platform.groups.finance.notAvailable')}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <AcademicSection analytics={analytics} locale={locale} />
    </>
  );
}

/**
 * The academic aggregates core relays from EduCore: the admissions funnel,
 * class-group capacity, attendance rate and cohort progression — per
 * institution and, where core aggregated them, as group totals.
 *
 * THE THREE STATES OF EVERY FIGURE. A number is core's figure, verbatim. null
 * means EduCore reported "no value" — a conversion rate with no applications,
 * an occupancy rate with no configured capacity — and renders as "Nothing to
 * compute" for rates and as an em dash for counts. Absent (undefined) means
 * the figure was not sent, and renders as an em dash, never as a zero. Group
 * totals only exist when every available institution reported the same count
 * (a partial total would under-count), so a missing total is a missing total.
 * Attendance rate and the last-updated timestamp are per institution by
 * contract and are never aggregated into a group figure.
 */
function AcademicSection({ analytics, locale }: { analytics: GroupAnalytics; locale: string }) {
  const { t } = useTranslation();
  const financial = analytics.financial;

  if (!financial) return null;

  const hasAcademic = financial.institutions.some(
    (institution) =>
      institution.available &&
      (institution.admissions !== undefined ||
        institution.capacity !== undefined ||
        institution.attendanceRate !== undefined ||
        institution.progression !== undefined ||
        institution.lastUpdatedAt !== undefined),
  );
  if (!hasAcademic) return null;

  const totals = financial.totals;
  const memberName = (institutionId: string): string => {
    const member = analytics.institutions.find((candidate) => candidate.institutionId === institutionId);
    return member ? member.name : institutionId;
  };
  const memberCode = (institutionId: string): string | undefined =>
    analytics.institutions.find((candidate) => candidate.institutionId === institutionId)?.code;

  const countCell = (value: number | null | undefined) =>
    value === undefined || value === null ? (
      <span className={styles.mutedCell}>—</span>
    ) : (
      formatCount(value, locale)
    );

  const rateCell = (rate: number | null | undefined) =>
    rate === undefined ? (
      <span className={styles.mutedCell}>—</span>
    ) : rate === null ? (
      <span className={styles.mutedCell}>{t('platform.groups.academics.nothingToCompute')}</span>
    ) : (
      t('platform.groups.academics.rateValue', { rate })
    );

  const notAvailableCell = (colSpan: number) => (
    <td colSpan={colSpan} className={styles.mutedCell}>
      {t('platform.groups.finance.notAvailable')}
    </td>
  );

  const memberHeader = (institutionId: string) => (
    <th scope="row" className={styles.tableRowHeader}>
      {memberName(institutionId)}
      {memberCode(institutionId) && <span className={styles.tableSub}>{memberCode(institutionId)}</span>}
    </th>
  );

  return (
    <>
      <p className={styles.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t('platform.groups.academics.lead')}
      </p>

      <h3 className={styles.subheading}>{t('platform.groups.academics.admissionsTitle')}</h3>
      {totals?.admissions && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <caption className={styles.tableCaption}>{t('platform.groups.academics.totalsTitle')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('platform.groups.academics.colApplications')}</th>
                <th scope="col">{t('platform.groups.academics.colOffered')}</th>
                <th scope="col">{t('platform.groups.academics.colAccepted')}</th>
                <th scope="col">{t('platform.groups.academics.colEnrolled')}</th>
                <th scope="col">{t('platform.groups.academics.colConversionRate')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{countCell(totals.admissions.applications)}</td>
                <td>{countCell(totals.admissions.offered)}</td>
                <td>{countCell(totals.admissions.accepted)}</td>
                <td>{countCell(totals.admissions.enrolled)}</td>
                <td>{rateCell(totals.admissions.conversionRate)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">{t('platform.groups.academics.colInstitution')}</th>
              <th scope="col">{t('platform.groups.academics.colApplications')}</th>
              <th scope="col">{t('platform.groups.academics.colOffered')}</th>
              <th scope="col">{t('platform.groups.academics.colAccepted')}</th>
              <th scope="col">{t('platform.groups.academics.colEnrolled')}</th>
              <th scope="col">{t('platform.groups.academics.colConversionRate')}</th>
            </tr>
          </thead>
          <tbody>
            {financial.institutions.map((institution) => (
              <tr key={institution.institutionId}>
                {memberHeader(institution.institutionId)}
                {institution.available ? (
                  <>
                    <td>{countCell(institution.admissions?.applications)}</td>
                    <td>{countCell(institution.admissions?.offered)}</td>
                    <td>{countCell(institution.admissions?.accepted)}</td>
                    <td>{countCell(institution.admissions?.enrolled)}</td>
                    <td>{rateCell(institution.admissions?.conversionRate)}</td>
                  </>
                ) : (
                  notAvailableCell(5)
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className={styles.subheading}>{t('platform.groups.academics.capacityTitle')}</h3>
      {totals?.capacity && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <caption className={styles.tableCaption}>{t('platform.groups.academics.totalsTitle')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('platform.groups.academics.colConfiguredGroups')}</th>
                <th scope="col">{t('platform.groups.academics.colCapacity')}</th>
                <th scope="col">{t('platform.groups.academics.colEnrolled')}</th>
                <th scope="col">{t('platform.groups.academics.colOccupancyRate')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{countCell(totals.capacity.configuredClassGroups)}</td>
                <td>{countCell(totals.capacity.totalCapacity)}</td>
                <td>{countCell(totals.capacity.totalEnrolled)}</td>
                <td>{rateCell(totals.capacity.occupancyRate)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">{t('platform.groups.academics.colInstitution')}</th>
              <th scope="col">{t('platform.groups.academics.colConfiguredGroups')}</th>
              <th scope="col">{t('platform.groups.academics.colCapacity')}</th>
              <th scope="col">{t('platform.groups.academics.colEnrolled')}</th>
              <th scope="col">{t('platform.groups.academics.colOccupancyRate')}</th>
            </tr>
          </thead>
          <tbody>
            {financial.institutions.map((institution) => (
              <tr key={institution.institutionId}>
                {memberHeader(institution.institutionId)}
                {institution.available ? (
                  <>
                    <td>{countCell(institution.capacity?.configuredClassGroups)}</td>
                    <td>{countCell(institution.capacity?.totalCapacity)}</td>
                    <td>{countCell(institution.capacity?.totalEnrolled)}</td>
                    <td>{rateCell(institution.capacity?.occupancyRate)}</td>
                  </>
                ) : (
                  notAvailableCell(4)
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className={styles.subheading}>{t('platform.groups.academics.attendanceProgressionTitle')}</h3>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">{t('platform.groups.academics.colInstitution')}</th>
              <th scope="col">{t('platform.groups.academics.colAttendanceRate')}</th>
              <th scope="col">{t('platform.groups.academics.colEligibleCohort')}</th>
              <th scope="col">{t('platform.groups.academics.colContinued')}</th>
              <th scope="col">{t('platform.groups.academics.colDidNotContinue')}</th>
              <th scope="col">{t('platform.groups.academics.colUnknownDestination')}</th>
              <th scope="col">{t('platform.groups.academics.colInternalProgressionRate')}</th>
              <th scope="col">{t('platform.groups.academics.colUpdated')}</th>
            </tr>
          </thead>
          <tbody>
            {financial.institutions.map((institution) => (
              <tr key={institution.institutionId}>
                {memberHeader(institution.institutionId)}
                {institution.available ? (
                  <>
                    <td>{rateCell(institution.attendanceRate)}</td>
                    <td>{countCell(institution.progression?.eligibleCohort)}</td>
                    <td>{countCell(institution.progression?.continuedWithinGroup)}</td>
                    <td>{countCell(institution.progression?.didNotContinueWithinGroup)}</td>
                    <td>{countCell(institution.progression?.unknownDestination)}</td>
                    <td>{rateCell(institution.progression?.internalProgressionRate)}</td>
                    <td className={styles.mutedCell}>{formatDateTime(institution.lastUpdatedAt, locale)}</td>
                  </>
                ) : (
                  notAvailableCell(7)
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totals?.progression && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <caption className={styles.tableCaption}>{t('platform.groups.academics.totalsTitle')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('platform.groups.academics.colEligibleCohort')}</th>
                <th scope="col">{t('platform.groups.academics.colContinued')}</th>
                <th scope="col">{t('platform.groups.academics.colDidNotContinue')}</th>
                <th scope="col">{t('platform.groups.academics.colUnknownDestination')}</th>
                <th scope="col">{t('platform.groups.academics.colInternalProgressionRate')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{countCell(totals.progression.eligibleCohort)}</td>
                <td>{countCell(totals.progression.continuedWithinGroup)}</td>
                <td>{countCell(totals.progression.didNotContinueWithinGroup)}</td>
                <td>{countCell(totals.progression.unknownDestination)}</td>
                <td>{rateCell(totals.progression.internalProgressionRate)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function GroupAnalyticsPanel({
  analyticsState,
  onRetry,
}: {
  analyticsState: AsyncState<GroupAnalytics>;
  onRetry: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const label = useEnumLabel();

  return (
    <Panel title={t('platform.groups.analytics.title')} icon="pulse">
      <AsyncPanel state={analyticsState} onRetry={onRetry}>
        {(analytics) => (
          <>
            <dl className={styles.detailGrid}>
              <dt>{t('platform.groups.analytics.institutions')}</dt>
              <dd>{formatCount(analytics.institutionCount, locale)}</dd>
              <dt>{t('platform.groups.analytics.active')}</dt>
              <dd>{formatCount(analytics.activeInstitutionCount, locale)}</dd>
              <dt>{t('platform.groups.analytics.campuses')}</dt>
              <dd>{formatCount(analytics.campusCount, locale)}</dd>
              <dt>{t('platform.groups.analytics.branches')}</dt>
              <dd>{formatCount(analytics.branchCount, locale)}</dd>
              {analytics.generatedAt && (
                <>
                  <dt>{t('platform.groups.analytics.generatedAt')}</dt>
                  <dd className={styles.mutedCell}>{formatDateTime(analytics.generatedAt, locale)}</dd>
                </>
              )}
            </dl>

            <h3 className={styles.subheading}>{t('platform.groups.analytics.membersTitle')}</h3>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">{t('platform.groups.analytics.colInstitution')}</th>
                    <th scope="col">{t('platform.groups.analytics.colStatus')}</th>
                    <th scope="col">{t('platform.groups.analytics.colProducts')}</th>
                    <th scope="col">{t('platform.groups.analytics.colSites')}</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.institutions.map((institution) => {
                    const subscriptions = institution.productSubscriptions ?? [];
                    return (
                      <tr key={institution.institutionId}>
                        <th scope="row" className={styles.tableRowHeader}>
                          {institution.name}
                          <span className={styles.tableSub}>{institution.code}</span>
                        </th>
                        <td>
                          {institution.status ? (
                            <span
                              className={
                                institution.status === 'ACTIVE'
                                  ? styles.okTag
                                  : institution.status === 'SUSPENDED'
                                    ? styles.dangerTag
                                    : styles.mutedTag
                              }
                            >
                              {label('state.onboarding', institution.status)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className={styles.mutedCell}>
                          {subscriptions.length > 0 ? subscriptions.join(', ') : '—'}
                        </td>
                        <td className={styles.mutedCell}>
                          {t('platform.groups.analytics.sitesValue', {
                            campuses: institution.campuses ?? 0,
                            branches: institution.branches ?? 0,
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </AsyncPanel>
    </Panel>
  );
}
